// components/ChinaSiteMap/index.jsx
// 站点地图画布：复用 ems-ours-backup（作者 Lumineeeeee）的官方中国 / 世界标准底图渲染，
// 数据源改为本地演示数据层（stationData），无任何后端依赖。
// 调用方传入站点列表 { id, name, lng, lat, state }，组件负责投影落点与官方底图绘制。
import React, { useEffect, useMemo, useState } from 'react';
import { Segmented, Spin } from 'antd';
import china from './china-provinces.json';
import { locateSites } from './siteGeography';
import MapCanvas from './MapCanvas';
import './ChinaSiteMap.css';

const MODE_OPTIONS = [
  { value: 'china', label: '中国地图' },
  { value: 'world', label: '世界地图' },
];

// 装机字段为展示字符串（如 '1.25 MW' / '2.50 MWh'），换算为 kW / kWh 供悬浮卡片使用
const toNumber = (value, factor = 1) => {
  const n = parseFloat(String(value).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n * factor : null;
};

const ChinaSiteMap = ({ sites = [], selectedId, onSelect, defaultMode = 'china' }) => {
  const [mode, setMode] = useState(defaultMode);
  const [world, setWorld] = useState(null);

  // 数据大屏配置里切换「中国地图 / 世界地图」并应用后，同步初始底图模式
  useEffect(() => { setMode(defaultMode); }, [defaultMode]);

  // 世界底图数据较大（约 1.2 MB），按需加载，避免拉高首屏体积
  useEffect(() => {
    if (mode !== 'world' || world) return undefined;
    let cancelled = false;
    import('./world-countries.json').then((module) => { if (!cancelled) setWorld(module.default); });
    return () => { cancelled = true; };
  }, [mode, world]);

  const data = mode === 'china' ? china : world;

  // 组装 locateSites 所需的行数据：WGS84 经纬度 + 装机 + 告警状态（演示口径）
  const located = useMemo(() => {
    const rows = sites.map((s) => ({
      // 兼容两种演示数据口径：STATIONS（id + rated 字符串）/ MAP_POINTS（key + 数值 kW）
      id: s.id != null ? s.id : s.key,
      station_name: s.name,
      location: [Number(s.lng), Number(s.lat)],
      capacity: Number.isFinite(s.capacity) ? s.capacity : (s.rated ? toNumber(s.rated.capacity, 1000) : null),
      power: Number.isFinite(s.power) ? s.power : (s.rated ? toNumber(s.rated.power, 1000) : null),
      has_active_alarm: s.state === 'online' ? 0 : 1,
    }));
    return locateSites(rows, china, world).sites;
  }, [sites, world]);

  const selectedSite = located.find((s) => s.id === selectedId) || null;

  return (
    <div className="china-site-map">
      <div className="china-map-toolbar">
        <Segmented size="small" value={mode} onChange={setMode} options={MODE_OPTIONS} />
        <span className="site-map-summary" role="status">
          {mode === 'china' ? '中国标准底图' : '世界标准底图'} · 可见站点 {located.length} 个
        </span>
      </div>
      {data ? (
        <>
          <MapCanvas
            key={mode}
            data={data}
            mode={mode}
            selected={null}
            onSelect={() => {}}
            sites={located}
            selectedSite={selectedSite}
            onSiteSelect={(site) => onSelect(site ? site.id : null)}
          />
          {/* 官方标准地图合规署名（审图号 + 来源），数据来自底图 JSON */}
          <div className="china-map-attribution">
            <a href={data.source} target="_blank" rel="noopener noreferrer">标准地图服务（天地图）</a>
            <span>审图号：{data.reviewNumber}</span>
            <span>底图：{mode === 'china' ? '中国' : '世界'}标准地图</span>
          </div>
        </>
      ) : (
        <div className="site-map-loading"><Spin /></div>
      )}
    </div>
  );
};

export default ChinaSiteMap;
